import { ErrorHandler, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalBroadcastService, MsalGuard, MsalInterceptor, MsalService } from '@azure/msal-angular';
import { AppComponent } from './app/app.component';
import { MSALGuardConfigFactory, MSALInstanceFactory, MSALInterceptorConfigFactory } from './app/auth/auth.config';
import { MockHttpInterceptor } from './app/auth/mock-http-interceptor';
import { graphqlProvider } from './app/graphql.provider';
import { GlobalErrorHandler } from './app/core/telemetry/global-error-handler';
import { TelemetryService } from './app/core/telemetry/telemetry.service';
import { DialogTelemetryService } from './app/core/telemetry/dialog-telemetry.service';
import { routes } from './app/routes';
import { environment } from './environments/environment';
import { FORM_ERRORS_ENABLED } from './form-errors.token';
import { SystemLockService } from './app/services/system-lock.service';
import { SystemLockInterceptor } from './app/services/system-lock.interceptor';
import { NoRolesInterceptor } from './app/auth/no-roles.interceptor';
import { SessionExpiryService } from './app/auth/session-expiry.service';

async function bootstrap() {
  // Configure providers based on mock auth setting
  const authProviders = environment.useMockAuth
    ? [
      // Mock authentication - no MSAL needed
      {
        provide: HTTP_INTERCEPTORS,
        useClass: MockHttpInterceptor,
        multi: true
      }
    ]
    : [
      // Real MSAL authentication - initialize MSAL first
      {
        provide: HTTP_INTERCEPTORS,
        useClass: MsalInterceptor,
        multi: true
      },
      {
        provide: MSAL_INSTANCE,
        useValue: await MSALInstanceFactory()
      },
      {
        provide: MSAL_GUARD_CONFIG,
        useFactory: MSALGuardConfigFactory
      },
      {
        provide: MSAL_INTERCEPTOR_CONFIG,
        useFactory: MSALInterceptorConfigFactory
      },
      MsalService,
      MsalGuard,
      MsalBroadcastService
    ];

  bootstrapApplication(AppComponent, {
    providers: [
      importProvidersFrom(BrowserModule),
      provideRouter(routes),
      provideAnimations(),
      provideHttpClient(withInterceptorsFromDi()),
      graphqlProvider,
      // Initialize Application Insights at startup (so request correlation is hooked early), and route
      // uncaught errors through the telemetry-aware global handler.
      // Fire-and-forget: init() fetches the runtime connection string, so we don't block app startup on it.
      provideAppInitializer(() => { void inject(TelemetryService).init(); }),
      // Record every Material dialog open as a telemetry event, from one app-wide subscription.
      provideAppInitializer(() => inject(DialogTelemetryService).init()),
      // Load the saving-lock status at startup and keep it polled, so editors, write buttons, the
      // Apollo mutation link, and the upload interceptor all read one reactive signal.
      provideAppInitializer(() => inject(SystemLockService).init()),
      // Start listening for MSAL token-acquisition failures app-wide, so an Entra session that expires
      // while a tab sits idle logs the user out to the login screen instead of leaving a zombie page.
      provideAppInitializer(() => inject(SessionExpiryService).init()),
      {
        provide: HTTP_INTERCEPTORS,
        useClass: SystemLockInterceptor,
        multi: true
      },
      { provide: ErrorHandler, useClass: GlobalErrorHandler },
      ...authProviders,
      {
        provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: {
          showDelay: 0,
          hideDelay: 0,
          touchGestures: 'auto',
          position: 'right',
          touchendHideDelay: 0
        }
      },
      // Dialogs block the page behind them, but the backdrop is see-through so a
      // dialog can be dragged aside and the page read while it stays open.
      {
        provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: {
          disableClose: true,
          backdropClass: 'dialog-backdrop--see-through'
        }
      },
      {
        provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
        useValue: {
          subscriptSizing: 'dynamic'
        }
      },
      { provide: FORM_ERRORS_ENABLED, useValue: environment.showFormErrors }
    ]
  })
    .catch(err => console.error(err));
}

bootstrap();
